import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NewClaim, SnackbarService, SnackbarType } from 'src/app/shared';
import { SaveAlertActionRequest } from 'src/app/shared/models/alert';
import { customvalidations } from 'src/app/shared/models/customvalidations.validator';
import { ActivityService } from '../../services/activity.service';
import { Session } from '../../services/session.service';
import { UserRoleService } from '../../services/user-role.service';
import { TranslationRequest } from 'src/app/shared/models/translation/translation-request';

@Component({
    selector: 'app-new-claims-edit',
    template: `
    <div class="container dialog-container">
      <form [formGroup]="formGroup" class="tw-flex tw-flex-wrap tw-w-full">
        <app-action-row class="tw-w-full tw-items-center">
          <h2 left1 class="app-header-2">{{'Confirmation' | translation:translationRequest}}</h2>
          <div right1 style="text-align: left">
            <button
              mat-icon-button
              (click)="closeDialog()"
              tabindex="0"
              [attr.aria-label]="'Close modal' | translation: translationRequest"
              >
              <mat-icon>clear</mat-icon>
            </button>
          </div>
        </app-action-row>
    
        @if (!review) {
          <div class="tw-w-full message">
            {{'Please review and update if needed. Press Next to continue.' | translation:translationRequest}}
          </div>
        }
        @if (review) {
          <div class="tw-w-full message">
            <span>{{ getReviewMessage() | translation:translationRequest }}</span>
          </div>
        }
    
            <app-value label="Claim" layout="col" class="tw-w-full app-pb1" [valueTemplate]="claimLabelTpl">
              <ng-template #claimLabelTpl>
                <app-claim-label
                  [claim]="newClaim"
                  [disabled]="true"
                  [hideWatchList]="true"
                  [noLink]="true"
                  layout="row"
                ></app-claim-label>
              </ng-template>
            </app-value>
            <app-value label="Name" layout="col" class="tw-w-full app-pb2" [valueTemplate]="claimantLabelTpl">
              <ng-template #claimantLabelTpl>
                <app-claimant-label
                  [claimant]="newClaim"
                  [disabled]="true"
                ></app-claimant-label>
              </ng-template>
            </app-value>
    
            @if (
              !this.editClicked &&
              review &&
              (newClaim.lob === 'DS' || newClaim.lob === 'LV')
              ) {
              <app-value
                label="First Day Absent"
                [value]="newClaim.firstDayAbsent | format : 'date'"
                layout="col"
                class="tw-w-full app-pb2"
                >
              </app-value>
            }
            @if (!this.editClicked && review && newClaim.lob === 'DS') {
              <app-value
                label="Last Day Worked"
                [value]="newClaim.lastDayWorked | format : 'date'"
                layout="col"
                class="tw-w-full app-pb2"
                >
              </app-value>
            }
    
            @if (
              !this.editClicked &&
              review &&
              newClaim.lob === 'LV' &&
              newClaim.source !== 'L'
              ) {
              <app-value
                label="Hours Worked"
                [value]="newClaim.hoursWorked | number"
                layout="col"
                class="tw-w-full app-pb2"
                >
              </app-value>
            }
    
            @if (
              this.editClicked && (newClaim.lob === 'DS' || newClaim.lob === 'LV')
              ) {
              <app-date-control
                formControlName="firstDayAbsent"
                [formGroup]="formGroup"
                label="First Day Absent"
                [validationControl]="'First day absent'"
                ngDefaultControl
                class="tw-w-full"
              ></app-date-control>
            }
    
            @if (this.editClicked && newClaim.lob === 'DS') {
              <app-date-control
                formControlName="lastDayWorked"
                [formGroup]="formGroup"
                label="Last Day Worked"
                [validationControl]="'Last day worked'"
                ngDefaultControl
                class="tw-w-full"
              ></app-date-control>
            }
    
            @if (this.editClicked && newClaim.lob === 'LV') {
              <app-text-control
                formControlName="hoursWorked"
                [formGroup]="formGroup"
                [validationControl]="'Hours worked'"
                label="Hours Worked"
                mask="0*.99"
                ngDefaultControl
                class="tw-w-full"
              ></app-text-control>
            }
            <div class="tw-w-full app-pt1"></div>
            @if (!review) {
              <app-action-row class="tw-w-full">
                <app-button
                  right2
                  emphasis="High"
                  type="submit"
                  (click)="validateForm()"
                  label="Next"
                  >
                </app-button>
                <app-button right1 emphasis="Low" (click)="closeDialog()" label="Cancel">
                </app-button>
              </app-action-row>
            }
    
            @if (review || newClaim.source === 'L') {
              <app-action-row
                class="tw-w-full"
                >
                @if (formGroup.valid || newClaim.source === 'L') {
                  <app-button
                    right3
                    emphasis="High"
                    (click)="dismissAlert()"
                    label="Confirm"
                    >
                  </app-button>
                }
                @if (
                  newClaim.lob !== 'WC' &&
                  newClaim.lob !== 'AD' &&
                  !isController &&
                  newClaim.source !== 'L'
                  ) {
                  <app-button
                    right2
                    emphasis="Low"
                    (click)="edit()"
                    label="Edit"
                    >
                  </app-button>
                }
                <app-button right1 emphasis="Low" (click)="closeDialog()" label="Cancel">
                </app-button>
              </app-action-row>
            }
          </form>
        </div>
    `,
    styles: [
        `
      .container {
        max-width: 400px;
        padding: 8px 16px 24px 16px;
      }
      .message {
        font-size: 14px;
        padding: 8px 0 24px 0px;
      }
    `,
    ],
    standalone: false
})
export class NewClaimsEditComponent {
  review: boolean = false;
  editClicked: boolean = false;
  isController: boolean = false;
  saveAlertRequest: SaveAlertActionRequest;
  formGroup: FormGroup;
  translationRequest = new TranslationRequest({
    language: sessionStorage.getItem('languagepreference'),
    key: null,
    contNum: 0,
    tableName: null,
    primaryKey: null,
    primaryValue: null,
    lob: null,
    group: 'Shared',
    subGroup: 'Shared',
  });

  constructor(
    private fb: FormBuilder,
    private activityService: ActivityService,
    private session: Session,
    private snackBarService: SnackbarService,
    public dialogRef: MatDialogRef<any>,
    private cdr: ChangeDetectorRef,
    private userRole: UserRoleService,
    @Inject(MAT_DIALOG_DATA) public newClaim: NewClaim
  ) {}

  ngOnInit(): void {
    this.initFormGroup();
    this.review = true;
    this.isController = this.userRole.controller;
    this.session.loadUser();
  }

  initFormGroup(): void {
    this.formGroup = this.fb.group({
      lastDayWorked: [this.getDateOrNull(this.newClaim.lastDayWorked)],
      firstDayAbsent: [this.getDateOrNull(this.newClaim.firstDayAbsent)],
      hoursWorked: [this.newClaim.hoursWorked],
    });
    if (this.newClaim.lob === 'DS') {
      this.formGroup
        .get('firstDayAbsent')
        ?.setValidators([
          customvalidations.required,
          customvalidations.dateGreaterThan(
            this.formGroup.get('lastDayWorked'),
            false,
            'First day absent cannot be before last day worked.'
          ),
        ]);
      this.formGroup
        .get('lastDayWorked')
        ?.setValidators([
          customvalidations.required,
          customvalidations.dateLessThan(
            this.formGroup.get('firstDayAbsent'),
            false,
            'Last day worked cannot be after first day absent.'
          ),
        ]);
    }

    if (this.newClaim.lob === 'LV') {
      this.formGroup
        .get('firstDayAbsent')
        ?.setValidators([customvalidations.required]);
      this.formGroup
        .get('hoursWorked')
        ?.setValidators([
          customvalidations.required,
          customvalidations.IsDecimal(),
          customvalidations.numberLessThan(
            8760,
            'Number of hours worked cannot exceed 8760'
          ),
        ]);
    }
    this.formGroup.updateValueAndValidity();
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  validateForm() {
    this.formGroup.markAllAsTouched();
    this.review = this.formGroup.valid;
  }

  closeDialog() {
    this.dialogRef.close();
  }

  dismissAlert() {
    if (this.formGroup.valid || this.newClaim.source == 'L') {
      const fda = this.formGroup.get('firstDayAbsent').value;
      const ldw = this.formGroup.get('lastDayWorked').value;
      this.saveAlertRequest = new SaveAlertActionRequest({
        alertMasterUid: Number(this.newClaim.alertId),
        action: 'DISMISSED - VOE',
        sourceSystem: this.newClaim.source,
        claimNumber: this.newClaim.claimNumber,
        claimId: this.newClaim.claimId,
        item: this.newClaim,
        type: 'NewClaim',
        examinerId: this.newClaim.examinerId,
        fdaDate: this.formGroup.get('firstDayAbsent').value
          ? typeof fda == 'object'
            ? fda.toLocaleDateString('en-US')
            : fda
          : null,
        hoursWorked: this.formGroup.get('hoursWorked').value,
        ldwDate: this.formGroup.get('lastDayWorked').value
          ? typeof ldw == 'object'
            ? ldw.toLocaleDateString('en-US')
            : ldw
          : null,
        lob: this.newClaim.lob,
        userOwns: this.newClaim.userOwns,
        NoteHeader: '',
      });
      if (
        this.session.user.emulatorContext &&
        this.session.user.emulatorContext.isEmulating
      ) {
        this.snackBarService.show(
          'Preview-only mode. Information not sent.',
          SnackbarType.Info
        );
      } else {
        this.activityService.saveAlertAction(this.saveAlertRequest);
        this.activityService.alertSelected.emit(
          this.saveAlertRequest.alertMasterUid.toString()
        );
      }
      this.dialogRef.close();
    }
  }

  edit() {
    this.editClicked = true;
    if (this.newClaim.lob === 'DS' || this.newClaim.lob === 'LV') {
      this.formGroup.controls['firstDayAbsent'].setValue(
        this.getDateOrNull(this.newClaim.firstDayAbsent)
      );
    }
    if (this.newClaim.lob === 'LV') {
      this.formGroup.controls['hoursWorked'].setValue(
        this.newClaim.hoursWorked
      );
    }
    if (this.newClaim.lob === 'DS') {
      this.formGroup.controls['lastDayWorked'].setValue(
        this.getDateOrNull(this.newClaim.lastDayWorked)
      );
    }
  }
  getDateOrNull(dateValue: any): Date | null {
    return dateValue instanceof Date
      ? dateValue
      : dateValue
      ? new Date(dateValue)
      : null;
  }

  getReviewMessage(): string {
    if (this.newClaim.lob === 'DS' || this.newClaim.lob === 'LV') {
      return 'Select Confirm to accept the information, Edit to make changes, or Cancel to return to the previous screen.';
    }
    return 'Select Confirm to acknowledge and dismiss the notification or Cancel to return to the previous screen.';
  }
}
